import { format, parse } from "date-fns";
import type { Locale } from "date-fns";
import {
  EnumOperatorOperationalStatus,
  EnumPromotionAccessOperatorStatus,
  EnumPromotionAvailabilityType,
  type PromotionFragmentFragment,
} from "gql/generated";

export const PROMOTION_DATE_FORMAT = "yyyy-MM-dd";

/** Mirrors the backend sentinel bounds used for ONGOING promotions. */
export const PROMOTION_MAX_DATE = "9999-12-31";

export type PromotionAvailabilityLike = {
  type: EnumPromotionAvailabilityType;
  dates?: Array<string | null> | null;
  startDate?: string | null;
  endDate?: string | null;
};

export const parsePromotionDate = (date: string) =>
  parse(date, PROMOTION_DATE_FORMAT, new Date());

export const todayPromotionDate = () =>
  format(new Date(), PROMOTION_DATE_FORMAT);

/**
 * True while the promotion's listing is temporarily or permanently closed:
 * visitors don't see the promotion (Decision 9), admins see a "Listing closed" tag.
 */
export const isPromotionListingClosed = (
  operationalStatus: EnumOperatorOperationalStatus | null | undefined,
): boolean =>
  operationalStatus === EnumOperatorOperationalStatus.TemporarilyClosed ||
  operationalStatus === EnumOperatorOperationalStatus.PermanentlyClosed;

/** The status a given organization has assigned to the promotion. */
export const getPromotionStatusForOrganization = (
  promotion: Pick<PromotionFragmentFragment, "access">,
  organizationId: string | undefined,
): EnumPromotionAccessOperatorStatus | undefined =>
  promotion.access.organizations.find(
    org => org?._entityId?.toString() === organizationId,
  )?.status ?? undefined;

/**
 * The status to show a listing user: approved anywhere wins, then for review,
 * then draft, then declined, then cancelled.
 */
const STATUS_PRIORITY: EnumPromotionAccessOperatorStatus[] = [
  EnumPromotionAccessOperatorStatus.Approved,
  EnumPromotionAccessOperatorStatus.ForReview,
  EnumPromotionAccessOperatorStatus.Draft,
  EnumPromotionAccessOperatorStatus.Declined,
  EnumPromotionAccessOperatorStatus.Cancelled,
];
export const getPromotionOverallStatus = (
  promotion: Pick<PromotionFragmentFragment, "access">,
): EnumPromotionAccessOperatorStatus => {
  const statuses = promotion.access.organizations
    .map(org => org?.status)
    .filter((s): s is EnumPromotionAccessOperatorStatus => !!s);
  return (
    STATUS_PRIORITY.find(status => statuses.includes(status)) ??
    EnumPromotionAccessOperatorStatus.Draft
  );
};

/** True when every available date is before today. */
export const isPromotionPast = (
  promotion: Pick<PromotionFragmentFragment, "lastDate">,
) => promotion.lastDate < todayPromotionDate();

const MAX_LISTED_DATES = 3;

/**
 * Compact date summary for cards and rows, e.g. "Sep 19 · 20 · 21 + 8 more",
 * "Sep 1 - Nov 30", or the ongoing label. Dates sharing a month are
 * collapsed to day numbers.
 */
export const formatPromotionDates = (
  availability: PromotionAvailabilityLike,
  labels: { ongoing: string; more: (count: number) => string },
  locale?: Locale,
  maxListedDates = MAX_LISTED_DATES,
): string => {
  const fmt = (date: string, pattern: string) =>
    format(parsePromotionDate(date), pattern, { locale });

  switch (availability.type) {
    case EnumPromotionAvailabilityType.Ongoing:
      return labels.ongoing;
    case EnumPromotionAvailabilityType.DateRange: {
      const { startDate, endDate } = availability;
      if (!startDate || !endDate) return "";
      const sameYear = startDate.slice(0, 4) === endDate.slice(0, 4);
      const thisYear =
        startDate.slice(0, 4) === todayPromotionDate().slice(0, 4);
      const pattern = sameYear && thisYear ? "MMM d" : "MMM d, yyyy";
      return `${fmt(startDate, pattern)} - ${fmt(endDate, pattern)}`;
    }
    case EnumPromotionAvailabilityType.SpecificDates:
    default: {
      const dates = (availability.dates ?? [])
        .filter((d): d is string => !!d)
        .sort();
      if (dates.length === 0) return "";
      const listed = dates.slice(0, maxListedDates);
      const parts: string[] = [];
      let previousMonth = "";
      for (const date of listed) {
        const month = date.slice(0, 7);
        parts.push(
          month === previousMonth ? fmt(date, "d") : fmt(date, "MMM d"),
        );
        previousMonth = month;
      }
      const remaining = dates.length - listed.length;
      return remaining > 0
        ? `${parts.join(" · ")} ${labels.more(remaining)}`
        : parts.join(" · ");
    }
  }
};

/** Every date the promotion is available, for the details view. Empty when ongoing. */
export const listPromotionDates = (
  availability: PromotionAvailabilityLike,
): string[] => {
  if (availability.type === EnumPromotionAvailabilityType.SpecificDates) {
    return (availability.dates ?? []).filter((d): d is string => !!d).sort();
  }
  return [];
};
